import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useAppendPinMutation } from "modules/AppointmentDetails/components/Interventions/hooks";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/components/TyreActions/components/ConnectInterventionModal/ConnectInterventionModal.scss";
import { useLinkReplacementMutation } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/components/TyreActions/components/ConnectInterventionModal/hooks";
import { getTyreDescription } from "modules/AppointmentDetails/components/VehicleTyres/utils";

import type { TFunction } from "i18next";
import type { Appointment, Intervention, VehicleTyreReplacement } from "models";

export type ConnectToInterventionPayload = {
  title: string;
  replacement: VehicleTyreReplacement;
};

type ConnectInterventionModalProps = {
  open: boolean;
  appointment: Appointment;
  connectToInterventionPayload: ConnectToInterventionPayload | null;
  onClose: () => void;
};

type NotConnectedInterventionsListProps = {
  t: TFunction;
  interventions: Intervention[] | undefined;
  onClick: (intervention_id: number) => void;
  isPending: boolean;
};

const NotConnectedInterventionsList = ({ t, interventions, onClick, isPending }: NotConnectedInterventionsListProps) => {
  const notConnectedInterventions = interventions?.filter(intervention => !intervention.question_result_id) ?? [];

  if (!notConnectedInterventions.length) return <span>{t("v8_no_interventions_to_connect", "There are no interventions to connect to")}</span>;

  return notConnectedInterventions.map(intervention => (
    <div key={intervention.id} className="ConnectInterventionModal__intervention">
      <span>{intervention.title}</span>
      <Button basic className="ConnectInterventionModal__linkButton" onClick={() => onClick(intervention.id)} loading={isPending} disabled={isPending}>
        <Icon className="link horizontal grey" />
      </Button>
    </div>
  ));
};

export const ConnectInterventionModal = ({ open, appointment, connectToInterventionPayload, onClose }: ConnectInterventionModalProps) => {
  const { t } = useTranslation();

  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);

  const { mutateAsync: linkReplacement, isPending } = useLinkReplacementMutation();
  const { mutateAsync: appendPin } = useAppendPinMutation();

  if (!connectToInterventionPayload) return null;

  const handleConnect = async (intervention_id: number) => {
    await linkReplacement({ intervention_id, vehicle_tyre_replacement_id: connectToInterventionPayload.replacement.id });
    onClose();

    const intervention = appointment.interventions?.find(i => i.id === intervention_id);
    const tyre = connectToInterventionPayload.replacement.tyre;
    if (!intervention?.pin_history?.length || !tyre || !canCreateFullPin) return;

    await appendPin({
      ...intervention.pin_history[0],
      intervention_id: intervention.id,
      note: t("v8_intervention_connected_message", "Replacement connected: {{title}}: {{replacement}}")
        .replace("{{title}}", connectToInterventionPayload.title)
        .replace("{{replacement}}", tyre.manufacturer + " " + getTyreDescription(tyre))
    });
  };

  return (
    <Modal open={open} className="ConnectInterventionModal">
      <Modal.Header className="ConnectInterventionModal__header">
        <span>{t("v8_connect_tyre_replacement_to_intervention", "Connect tyre replacement to intervention")}</span>
        <Button className="modal-close-btn" basic onClick={onClose}>
          <Icon className="xmark grey" />
        </Button>
      </Modal.Header>

      <Modal.Content>
        <NotConnectedInterventionsList t={t} interventions={appointment.interventions} onClick={handleConnect} isPending={isPending} />
      </Modal.Content>
    </Modal>
  );
};
